import { resolveWorkspaceAgentSelectAssetHighlightIndex } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/select-asset/resolveWorkspaceAgentSelectAssetHighlightIndex';
import { type WorkspaceAgentMentionRowItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import { isWorkspaceAgentMentionTreeItem } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionItems';
import { GeneralLibrary } from '~/utils/librariesUtils';

/** The board the choose-board menu should open on. Ancestors are root-first, matching breadcrumbs. */
export type WorkspaceAgentGuidedVariantChooseBoardTarget = {
  ancestorIds: readonly string[];
  id: string;
  libraryId: string | undefined;
};

const isNamedLibraryId = (libraryId: string | undefined): libraryId is string =>
  !!libraryId && libraryId !== GeneralLibrary.id;

/**
 * Open the library, every ancestor, and the current board. A named library starts closed, and the
 * board's own children are what "expanded" means once the row is on screen.
 */
export const getWorkspaceAgentGuidedVariantChooseBoardExpandedNodeIds = (
  board: WorkspaceAgentGuidedVariantChooseBoardTarget,
): readonly string[] => {
  const libraryNodeId = isNamedLibraryId(board.libraryId) ? board.libraryId : GeneralLibrary.id;

  return [...new Set([libraryNodeId, ...board.ancestorIds, board.id])];
};

const isChooseBoardPathBoardVisible = (items: readonly WorkspaceAgentMentionRowItem[], boardId: string): boolean =>
  items.some((item) => isWorkspaceAgentMentionTreeItem(item) && item.kind === 'board' && item.boardId === boardId);

/**
 * The list to page next so the current board can appear. Walks library → ancestors → board and
 * returns the first parent whose child is still missing. A later ancestor never loads if an earlier
 * one is still off-page.
 */
export const getWorkspaceAgentGuidedVariantChooseBoardPendingListNodeId = (
  board: WorkspaceAgentGuidedVariantChooseBoardTarget,
  items: readonly WorkspaceAgentMentionRowItem[],
): string | undefined => {
  const libraryNodeId = isNamedLibraryId(board.libraryId) ? board.libraryId : GeneralLibrary.id;
  const pathIds = [...board.ancestorIds, board.id];

  let listNodeId = libraryNodeId;

  for (const boardId of pathIds) {
    if (!isChooseBoardPathBoardVisible(items, boardId)) {
      return listNodeId;
    }

    listNodeId = boardId;
  }

  return undefined;
};

export type ResolveWorkspaceAgentGuidedVariantChooseBoardHighlightIndexParams = {
  currentBoardId: string | undefined;
  hasUserMovedHighlight: boolean;
  nextItems: readonly WorkspaceAgentMentionRowItem[];
  previousIndex: number;
  previousItems: readonly WorkspaceAgentMentionRowItem[];
  query: string;
};

/**
 * Until the user moves the highlight, keep it on the current board once that row exists.
 * A typed query and a moved highlight fall through to the shared list resolver.
 */
export const resolveWorkspaceAgentGuidedVariantChooseBoardHighlightIndex = ({
  currentBoardId,
  hasUserMovedHighlight,
  nextItems,
  previousIndex,
  previousItems,
  query,
}: ResolveWorkspaceAgentGuidedVariantChooseBoardHighlightIndexParams): number => {
  const resolvedIndex = resolveWorkspaceAgentSelectAssetHighlightIndex({
    nextItems,
    previousIndex,
    previousItems,
    query,
  });

  if (query !== '' || hasUserMovedHighlight || !currentBoardId) {
    return resolvedIndex;
  }

  const currentBoardIndex = nextItems.findIndex(
    (item) => isWorkspaceAgentMentionTreeItem(item) && item.kind === 'board' && item.boardId === currentBoardId,
  );

  return currentBoardIndex >= 0 ? currentBoardIndex : resolvedIndex;
};
